import uuidv4 from "uuid/v4";

import modal from "../shared/actions";
import {
  ADD_CHANGESET_FAILED,
  ADD_CHANGESET_START,
  ADD_CHANGESET_SUCCESS,
  ADD_GLOBAL_ADMIN_FAILED,
  ADD_GLOBAL_ADMIN_START,
  ADD_GLOBAL_ADMIN_SUCCESS,
  ADD_GROUP_FAILED,
  ADD_GROUP_START,
  ADD_GROUP_SUCCESS,
  ADD_POLICY,
  ADD_SERVICE_FAILED,
  ADD_SERVICE_START,
  ADD_SERVICE_SUCCESS,
  ADD_SERVICE_WITH_ADMIN_FAILED,
  ADD_SERVICE_WITH_ADMIN_START,
  ADD_SERVICE_WITH_ADMIN_SUCCESS,
  ADD_USER_FAILED,
  ADD_USER_START,
  ADD_USER_SUCCESS,
  COMMIT_ADD_POLICIES_SUCCESS,
  COMMIT_DELETE_POLICIES_SUCCESS,
  COMMIT_POLICIES_FAILED,
  COMMIT_POLICIES_START,
  FETCH_CHANGESETS,
  FETCH_GROUPS,
  FETCH_PERMISSIONS,
  FETCH_POLICIES,
  FETCH_PRODUCTS,
  FETCH_RESOURCE_TYPES,
  FETCH_RESOURCES,
  FETCH_ROLES,
  FETCH_SERVICES,
  FETCH_USERS,
  MARK_POLICY_CLEAN,
  MARK_POLICY_FOR_DELETE,
  MARK_POLICY_FOR_UPDATE,
  REGISTER_SERVICE_FAILED,
  REGISTER_SERVICE_START,
  REGISTER_SERVICE_SUCCESS,
  REMOVE_DIRTY_POLICY,
  REMOVE_GROUP_FAILED,
  REMOVE_GROUP_START,
  REMOVE_GROUP_SUCCESS,
  REMOVE_POLICY_CANCEL,
  REMOVE_POLICY_FAILED,
  REMOVE_POLICY_START,
  REMOVE_POLICY_SUCCESS,
  REMOVE_RESOURCE,
  REMOVE_SERVICE_FAILED,
  REMOVE_SERVICE_START,
  REMOVE_SERVICE_SUCCESS,
  REMOVE_USER_FAILED,
  REMOVE_USER_START,
  REMOVE_USER_SUCCESS,
  SET_CURRENT_GROUP,
  SET_CURRENT_PRODUCT,
  SET_CURRENT_RESOURCE,
  SET_CURRENT_RESOURCE_TYPE,
  SET_CURRENT_ROLE,
  SET_CURRENT_SERVICE,
  SET_CURRENT_USER,
  SET_FIRST_RESOURCE_IN_SERVICE,
  SET_ROLES_FILTER_TERM,
  SET_SERVICES_FILTER_TERM,
  SET_USERS_GROUPS_FILTER_TERM
} from "./constants";

const START = "START";
const SUCCESS = "SUCCESS";
const FAILED = "FAILED";

export const createRequestTypes = base =>
  [START, SUCCESS, FAILED].reduce((acc, type) => {
    acc[type] = `${base}_${type}`;
    return acc;
  }, {});

export const PRODUCTS = createRequestTypes(FETCH_PRODUCTS);
export const SERVICES = createRequestTypes(FETCH_SERVICES);
export const RESOURCES = createRequestTypes(FETCH_RESOURCES);
export const RESOURCE_TYPES = createRequestTypes(FETCH_RESOURCE_TYPES);
export const USERS = createRequestTypes(FETCH_USERS);
export const GROUPS = createRequestTypes(FETCH_GROUPS);
export const ROLES = createRequestTypes(FETCH_ROLES);
export const POLICIES = createRequestTypes(FETCH_POLICIES);
export const PERMISSIONS = createRequestTypes(FETCH_PERMISSIONS);
export const CHANGESETS = createRequestTypes(FETCH_CHANGESETS);

function action(type, payload = {}) {
  return { type, ...payload };
}

export const fetchActionFactory = requestTypes => ({
  request: () => action(requestTypes[START]),
  success: payload => action(requestTypes[SUCCESS], { json: payload }),
  failed: error =>
    action(requestTypes[FAILED], {
      error: {
        id: uuidv4(),
        message: error.message,
        description: `Unable to request data from API.`
      }
    })
});

/* products */
const products = fetchActionFactory(PRODUCTS);
products.setCurrent = productUri =>
  action(SET_CURRENT_PRODUCT, { uri: productUri });
products.unsetCurrent = () => action(SET_CURRENT_PRODUCT, { uri: undefined });

/* services */
const services = fetchActionFactory(SERVICES);
services.setCurrent = serviceUri =>
  action(SET_CURRENT_SERVICE, { uri: serviceUri });
services.unsetCurrent = () => action(SET_CURRENT_SERVICE, { uri: undefined });
services.setFilterTerm = filterTerm =>
  action(SET_SERVICES_FILTER_TERM, { filterTerm });
services.add = service => action(ADD_SERVICE_START, { service });
services.addSuccess = service => action(ADD_SERVICE_SUCCESS, { service });
services.addFailed = error => {
  const description =
    error.code === 409
      ? "Service already exists, please try another name."
      : "Failed to add service.";
  return action(ADD_SERVICE_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description
    }
  });
};
services.addWithAdmin = (service, username, groupname) =>
  action(ADD_SERVICE_WITH_ADMIN_START, { service, username, groupname });
services.addWithAdminSuccess = (service, user) =>
  action(ADD_SERVICE_WITH_ADMIN_SUCCESS, { service, user });
services.addWithAdminFailed = error => {
  const description =
    error.code === 409
      ? "Service already exists, please try another name."
      : "Failed to add service.";
  return action(ADD_SERVICE_WITH_ADMIN_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description
    }
  });
};
services.remove = service => action(REMOVE_SERVICE_START, { service });
services.removeSuccess = service => action(REMOVE_SERVICE_SUCCESS, { service });
services.removeFailed = error =>
  action(REMOVE_SERVICE_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description: "Failed to remove service."
    }
  });
services.register = (service, keyPair) =>
  action(REGISTER_SERVICE_START, { service, keyPair });
services.registerSuccess = (service, response) =>
  action(REGISTER_SERVICE_SUCCESS, { service, response });
services.registerFailed = error =>
  action(REGISTER_SERVICE_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description: "Failed to register service."
    }
  });

/* resources */
const resources = fetchActionFactory(RESOURCES);
resources.setCurrent = resourceUri =>
  action(SET_CURRENT_RESOURCE, { uri: resourceUri });
resources.unsetCurrent = () => action(SET_CURRENT_RESOURCE, { uri: undefined });
resources.setFirstInService = serviceUri =>
  action(SET_FIRST_RESOURCE_IN_SERVICE, { serviceUri });
resources.remove = resource => action(REMOVE_RESOURCE, { resource });

/* resourceTypes */
const resourceTypes = fetchActionFactory(RESOURCE_TYPES);
resourceTypes.setCurrent = resourceTypeUri =>
  action(SET_CURRENT_RESOURCE_TYPE, { uri: resourceTypeUri });
resourceTypes.unsetCurrent = () =>
  action(SET_CURRENT_RESOURCE_TYPE, { uri: undefined });

/* users */
const users = fetchActionFactory(USERS);
users.setFilterTerm = filterTerm =>
  action(SET_USERS_GROUPS_FILTER_TERM, { filterTerm });
users.setCurrent = userUri => action(SET_CURRENT_USER, { uri: userUri });
users.add = username => action(ADD_USER_START, { username });
users.addSuccess = user => action(ADD_USER_SUCCESS, { user });
users.addFailed = error => {
  const description =
    error.code === 409
      ? "User already exists, please try another username."
      : "Failed to add user.";
  return action(ADD_USER_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description
    }
  });
};
users.remove = user => action(REMOVE_USER_START, { user });
users.removeSuccess = user => action(REMOVE_USER_SUCCESS, { user });
users.removeFailed = error =>
  action(REMOVE_USER_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description: "Failed to remove user."
    }
  });
users.openAddModal = () => modal.open("ADD_USER");
users.closeModal = () => modal.close();

/* groups */
const groups = fetchActionFactory(GROUPS);
groups.setFilterTerm = filterTerm =>
  action(SET_USERS_GROUPS_FILTER_TERM, { filterTerm });
groups.add = name => action(ADD_GROUP_START, { name });
groups.addSuccess = group => action(ADD_GROUP_SUCCESS, { group });
groups.addFailed = error => {
  const description =
    error.code === 409
      ? "Group already exists, please try another name."
      : "Failed to add group.";
  return action(ADD_GROUP_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description
    }
  });
};
groups.remove = group => action(REMOVE_GROUP_START, { group });
groups.removeSuccess = group => action(REMOVE_GROUP_SUCCESS, { group });
groups.removeFailed = error =>
  action(REMOVE_GROUP_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description: "Failed to remove group."
    }
  });
groups.openAddModal = () => modal.open("ADD_GROUP");
groups.closeModal = () => modal.close();
groups.setCurrent = groupUri => action(SET_CURRENT_GROUP, { uri: groupUri });

/* roles */
const roles = fetchActionFactory(ROLES);
roles.setFilterTerm = filterTerm =>
  action(SET_ROLES_FILTER_TERM, { filterTerm });
roles.setCurrent = roleUri => action(SET_CURRENT_ROLE, { uri: roleUri });

/* policies */
const policies = fetchActionFactory(POLICIES);
policies.add = policy => action(ADD_POLICY, { policy });
policies.removeDirty = policy => action(REMOVE_DIRTY_POLICY, { policy });
policies.remove = policy => action(REMOVE_POLICY_START, { policy });
policies.removeSuccess = policy => action(REMOVE_POLICY_SUCCESS, { policy });
policies.removeCancel = policy => action(REMOVE_POLICY_CANCEL, { policy });
policies.removeFailed = (error, policy) =>
  action(REMOVE_POLICY_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description: "Failed to remove policy."
    },
    policy
  });
policies.markForUpdate = policy => action(MARK_POLICY_FOR_UPDATE, { policy });
policies.markForDelete = policy => action(MARK_POLICY_FOR_DELETE, { policy });
policies.markClean = policy => action(MARK_POLICY_CLEAN, { policy });
policies.commit = (policies, message) =>
  action(COMMIT_POLICIES_START, { policies, message });
policies.commitAddSuccess = policies =>
  action(COMMIT_ADD_POLICIES_SUCCESS, { policies });
policies.commitDeleteSuccess = policies =>
  action(COMMIT_DELETE_POLICIES_SUCCESS, { policies });
policies.commitFailed = error =>
  action(COMMIT_POLICIES_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description: "Failed to save policies."
    }
  });
policies.addGlobalAdmin = (roleLabel, entityType, name) =>
  action(ADD_GLOBAL_ADMIN_START, { roleLabel, entityType, name });
policies.addGlobalAdminSuccess = () => action(ADD_GLOBAL_ADMIN_SUCCESS);
policies.addGlobalAdminFailed = error =>
  action(ADD_GLOBAL_ADMIN_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description: "Failed to save admin policy."
    }
  });
policies.openDeleteModal = args => modal.open("DELETE_POLICIES", { ...args });
policies.closeModal = () => modal.close();

/* permissions */
const permissions = fetchActionFactory(PERMISSIONS);

/* changesets */
const changesets = fetchActionFactory(CHANGESETS);
changesets.add = message => action(ADD_CHANGESET_START, message);
changesets.addSuccess = changeset =>
  action(ADD_CHANGESET_SUCCESS, { changeset });
changesets.addFailed = error =>
  action(ADD_CHANGESET_FAILED, {
    error: {
      id: uuidv4(),
      message: error.message,
      description: "Failed to add changeset."
    }
  });

export {
  changesets,
  users,
  groups,
  policies,
  roles,
  products,
  services,
  resources,
  resourceTypes,
  permissions
};
